/**
 * KAN-743: карточка товара каталога (только просмотр). Хлебные крошки по
 * category_path ведут в каталог с категорией. Описание выводится текстом
 * (HTML фида не рендерится). Внизу сворачиваемая таблица всех полей фида.
 * KAN-754: ручной товар помечен, владелец его меняет, снимает с продажи и
 * удаляет.
 * KAN-761: заметная кнопка "В заказ" у товара в продаже (диалог AddToOrderDialog).
 * KAN-755: блок "Скидки на товар" (ProductDiscountsBlock).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { ProductThumb } from '@/components/catalog/ProductThumb';
import { AvailabilityBadge } from '@/components/catalog/CatalogProductsTable';
import { ManualBadge } from '@/components/catalog/ManualBadge';
import { ManualProductActions } from '@/components/catalog/ManualProductActions';
import { AddToOrderButton, AddToOrderFlowDialogs } from '@/components/catalog/AddToOrderDialog';
import { ProductDiscountsBlock } from '@/components/discounts/ProductDiscountsBlock';
import { canOrderProduct, useAddToOrder } from '@/hooks/useAddToOrder';
import { useIsOwner } from '@/hooks/useCurrentUser';
import { isNotFoundError, useCatalogProduct } from '@/hooks/useCatalog';
import { descriptionToText, formatFeedDate, formatPrice, rawFieldEntries } from '@/lib/catalogTree';
import { safeHttpUrl } from '@/lib/safeUrl';
import type { CatalogProductDetail } from '@/types/catalog';

const categoryHref = (id: string | number): string => `/catalog?category=${encodeURIComponent(String(id))}`;

function RawFields({ raw }: { raw: CatalogProductDetail['raw'] }) {
  const [open, setOpen] = useState(false);
  const entries = useMemo(() => rawFieldEntries(raw), [raw]);
  if (entries.length === 0) return null;

  return (
    <section className="rounded-lg border bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-gray-800 hover:bg-gray-50"
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        Все поля из фида ({entries.length})
      </button>
      {open && (
        <div className="overflow-x-auto border-t">
          <table data-testid="raw-fields" className="w-full text-sm">
            <tbody>
              {entries.map(([field, value]) => (
                <tr key={field} className="border-b last:border-b-0 align-top">
                  <th scope="row" className="w-1/3 px-4 py-2 text-left font-mono text-xs font-normal text-gray-500 break-all">
                    {field}
                  </th>
                  <td className="px-4 py-2 whitespace-pre-wrap break-words text-gray-800 [overflow-wrap:anywhere]">
                    {value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap gap-x-2 text-sm">
      <dt className="text-gray-500">{label}:</dt>
      <dd className="min-w-0 text-gray-900 [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

interface ProductBodyProps {
  product: CatalogProductDetail;
  canEdit: boolean;
  onDeleted: () => void;
}

function ProductBody({ product, canEdit, onDeleted }: ProductBodyProps) {
  const description = descriptionToText(product.description);
  const updated = formatFeedDate(product.updated_at);
  const siteUrl = safeHttpUrl(product.url);
  const addToOrder = useAddToOrder();

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
        <div className="flex items-center justify-center rounded-lg border bg-white p-4">
          <ProductThumb
            src={product.picture_url}
            alt={product.name}
            className="aspect-square h-auto w-full max-w-[280px]"
            iconClassName="h-12 w-12"
          />
        </div>
        <div className="space-y-4 rounded-lg border bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-2xl font-semibold text-gray-900">{formatPrice(product.price, product.currency)}</div>
            {canOrderProduct(product) && <AddToOrderButton prominent onClick={() => addToOrder.open(product)} />}
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
            <AvailabilityBadge available={product.available} />
            {product.manual && <ManualBadge />}
            {product.stock_quantity !== null && product.stock_quantity !== undefined && (
              <span>Остаток: {product.stock_quantity}</span>
            )}
          </div>
          <dl className="space-y-1.5">
            <Field label="Артикул">{product.sku || '-'}</Field>
            {product.category_name && <Field label="Категория">{product.category_name}</Field>}
            {product.feed_source && <Field label="Источник">{product.feed_source.name}</Field>}
            {updated && <Field label="Обновлено">{updated}</Field>}
          </dl>
          {canEdit && <ManualProductActions product={product} onDeleted={onDeleted} />}
          {siteUrl && (
            <a
              href={siteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline"
            >
              Открыть на сайте
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
      </div>

      <ProductDiscountsBlock discounts={product.discounts} />

      <section className="rounded-lg border bg-white p-4">
        <h2 className="mb-2 text-sm font-medium text-gray-800">Описание</h2>
        {description ? (
          <p data-testid="product-description" className="whitespace-pre-line text-sm text-gray-700 [overflow-wrap:anywhere]">
            {description}
          </p>
        ) : (
          <p className="text-sm text-gray-400">Описания нет</p>
        )}
      </section>

      <RawFields raw={product.raw} />
      <AddToOrderFlowDialogs flow={addToOrder} />
    </div>
  );
}

export function CatalogProductPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const backTo = (location.state as { backTo?: string } | null)?.backTo ?? '/catalog';
  const { data: product, isLoading, error } = useCatalogProduct(id);
  const isOwner = useIsOwner();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = `${product?.name ?? 'Товар'} - SMETA AI`;
  }, [product?.name]);

  const breadcrumbs = [
    { label: 'Каталог', href: '/catalog' },
    ...(product?.category_path ?? []).map((c) => ({ label: c.name, href: categoryHref(c.id) })),
    ...(product ? [{ label: product.name }] : []),
  ];

  let content: JSX.Element;
  if (isLoading) {
    content = <p className="text-sm text-gray-500">Загрузка...</p>;
  } else if (error || !product) {
    content = (
      <div className="rounded-lg border bg-white p-6 text-sm text-gray-600">
        <p className="mb-2 font-medium text-gray-900">
          {isNotFoundError(error) ? 'Товар не найден' : 'Не удалось загрузить товар'}
        </p>
        <Link to="/catalog" className="text-blue-600 hover:underline">
          Вернуться в каталог
        </Link>
      </div>
    );
  } else {
    content = <ProductBody product={product} canEdit={isOwner} onDeleted={() => navigate(backTo, { replace: true })} />;
  }

  return (
    <PageLayout
      title={product?.name ?? 'Товар'}
      breadcrumbs={breadcrumbs}
      backButton={{ href: backTo }}
      maxWidth="max-w-5xl"
      stickyHeader={false}
      data-testid="catalog-product-page"
    >
      {content}
    </PageLayout>
  );
}
